<template>
    <v-row justify="center">
        <PlantConfigDialog
            :dialog.sync="configPlantDialog"
            :plantNodeId="
                configingPlant && configingPlant.node
                    ? configingPlant.node.id
                    : null
            "
            v-on:item-added="onPlantConfirmed"
        ></PlantConfigDialog>

        <v-dialog v-model="dialog" persistent max-width="1100px" scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    Add Growth Task
                    <v-spacer />
                    <v-btn icon dark @click="closeMainDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-stepper v-model="step" vertical>
                        <!-- Step 1: select plant -->
                        <v-stepper-step step="1" :complete="step > 1">
                            <span v-if="selectedPlant === null"
                                >Select a plant</span
                            >
                            <span
                                v-if="selectedPlant"
                                class="grey--text text--darken-4 mb-2"
                                >{{
                                    selectedPlant
                                        ? selectedPlant.plant.name
                                        : ""
                                }}</span
                            >
                            <v-row v-if="selectedPlant" class="px-2">
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>straighten</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant
                                            ? selectedPlant.potSize.potSizeCm
                                            : "-"
                                    }}cm
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>color_lens</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant
                                            ? selectedPlant.color.color
                                            : "-"
                                    }}
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>blur_on</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant
                                            ? selectedPlant.environment
                                                  .environment
                                            : "-"
                                    }}
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>turned_in_not</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant && selectedPlant.tag
                                            ? selectedPlant.tag.tag
                                            : "-"
                                    }}
                                </v-chip>
                                <v-spacer />
                            </v-row>
                        </v-stepper-step>

                        <v-stepper-content step="1">
                            <v-form ref="formStep1">
                                <v-card flat>
                                    <!-- Choices table -->
                                    <v-row>
                                        <!-- Header -->
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="search"
                                                append-outer-icon="search"
                                                label="Search"
                                                clearable
                                                hide-details
                                                @keydown.enter="
                                                    refreshCurrentPlantsPage()
                                                "
                                                @click:append-outer="
                                                    refreshCurrentPlantsPage()
                                                "
                                                @click:clear="onClearSearch()"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-data-table
                                                :headers="headers"
                                                :loading="
                                                    $apollo.queries.tenantPlants
                                                        .loading
                                                "
                                                :items="tenantPlants.edges"
                                                :options.sync="dataTableOptions"
                                                loading-text="Loading... Please wait"
                                                :server-items-length="
                                                    tenantPlants
                                                        ? tenantPlants.totalCount
                                                        : 0
                                                "
                                                :items-per-page="
                                                    defaultItemsPerPage
                                                "
                                                :footer-props="{
                                                    'items-per-page-options': [
                                                        10,
                                                        25,
                                                        50,
                                                        75,
                                                        100
                                                    ],
                                                    'disable-pagination':
                                                        $apollo.queries
                                                            .tenantPlants
                                                            .loading
                                                }"
                                            >
                                                <template
                                                    v-slot:item.node.numProducts="{
                                                        item
                                                    }"
                                                >
                                                    <span
                                                        :class="
                                                            item.node
                                                                .numProducts <=
                                                            0
                                                                ? 'red--text'
                                                                : ''
                                                        "
                                                        >{{
                                                            item.node
                                                                .numProducts
                                                        }}</span
                                                    >
                                                </template>
                                                <template
                                                    v-slot:item.action="{
                                                        item
                                                    }"
                                                >
                                                    <v-btn
                                                        small
                                                        outlined
                                                        rounded
                                                        color="primary"
                                                        @click="
                                                            onConfigPlant(item)
                                                        "
                                                        >Select</v-btn
                                                    >
                                                    <v-btn
                                                        icon
                                                        color="primary"
                                                        @click="
                                                            goToPlantDetail(
                                                                item.node.id
                                                            )
                                                        "
                                                    >
                                                        <v-icon>launch</v-icon>
                                                    </v-btn>
                                                </template>
                                            </v-data-table>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 2: quantity and supplier -->
                        <v-stepper-step step="2" :complete="step > 2">
                            Quantity and supplier
                            <span
                                v-if="step > 2"
                                class="mt-1 grey--text text--darken-2 subtitle-2"
                                >{{ totalQuantity }} plants</span
                            >
                        </v-stepper-step>

                        <v-stepper-content step="2">
                            <v-form ref="formStep2">
                                <v-card flat>
                                    <v-row class="align-center">
                                        <v-col cols="12" sm="3">
                                            <v-text-field
                                                v-model="totalQuantity"
                                                label="Quantity*"
                                                :rules="[rules.required]"
                                                min="1"
                                                type="number"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="9">
                                            <v-text-field
                                                v-model="note"
                                                label="Note"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-text-field
                                                v-model="supplierOrderNo"
                                                label="Supplier Order No"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-text-field
                                                v-model="unitPurchasePrice"
                                                prepend-inner-icon="euro_symbol"
                                                label="Purchase Price (unit)"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                v-model="supplierNodeId"
                                                :items="suppliers.edges"
                                                item-text="node.name"
                                                item-value="node.id"
                                                :loading="
                                                    $apollo.queries.suppliers
                                                        .loading
                                                "
                                                label="Supplier"
                                                class="subtitle-2 mb-n5"
                                                outlined
                                            ></v-autocomplete>
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn
                                        text
                                        rounded
                                        @click="
                                            step = 1;
                                            selectedPlant = null;
                                        "
                                        >Previous</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        @click="step2Next"
                                        >Continue</v-btn
                                    >
                                </v-card-actions>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 3: growth phases -->
                        <v-stepper-step step="3" :complete="step > 3">
                            Phases
                            <span
                                v-if="step > 3"
                                class="mt-1 grey--text text--darken-2 subtitle-2"
                                >{{ growthTask.numPhases }} phases,
                                {{ totalGrowthDays }} days</span
                            >
                        </v-stepper-step>

                        <v-stepper-content step="3">
                            <v-form ref="formStep3">
                                <v-card flat>
                                    <v-row class="align-center">
                                        <v-col cols="12" sm="3" class="ml-3">
                                            <v-switch
                                                v-model="
                                                    isImportPhasesFromTemplate
                                                "
                                                label="Import from template"
                                            ></v-switch>
                                        </v-col>

                                        <v-spacer />
                                        <!-- Number of Phases -->
                                        <v-col
                                            cols="6"
                                            sm="4"
                                            v-if="!isImportPhasesFromTemplate"
                                        >
                                            <v-select
                                                v-model="growthTask.numPhases"
                                                :items="numPhasesChoices"
                                                :rules="[rules.required]"
                                                label="# Phases*"
                                                :disabled="
                                                    selectedTemplateNodeId !==
                                                        null
                                                "
                                                outlined
                                                :menu-props="{ offsetY: true }"
                                                class="subtitle-2 mb-n5"
                                            ></v-select>
                                        </v-col>
                                        <!-- select tempalte -->
                                        <v-col cols="12" sm="4" v-else>
                                            <v-autocomplete
                                                v-model="selectedTemplateNodeId"
                                                :items="
                                                    sortedSelectedPlantGrowthTemplates
                                                "
                                                :rules="[
                                                    isImportPhasesFromTemplate
                                                        ? rules.required
                                                        : null
                                                ]"
                                                item-value="node.id"
                                                item-text="node.name"
                                                label="Template *"
                                                class="subtitle-2 mb-n5"
                                                outlined
                                            >
                                            </v-autocomplete>
                                        </v-col>

                                        <!-- Max Sales Days -->
                                        <v-col cols="6" sm="3">
                                            <v-select
                                                v-model="
                                                    growthTask.sales.numDays
                                                "
                                                :items="numSaleDaysChoices"
                                                :rules="[rules.required]"
                                                label="#Sale Days*"
                                                outlined
                                                :menu-props="{ offsetY: true }"
                                                class="subtitle-2 mb-n5"
                                            ></v-select>
                                        </v-col>
                                    </v-row>

                                    <!-- Phases -->
                                    <v-row>
                                        <v-card-text
                                            class="pt-0 font-weight-medium"
                                        >
                                            <v-row align="center">
                                                <v-divider></v-divider>
                                                <span class="pl-3 pr-3"
                                                    >Phases</span
                                                >
                                                <v-divider></v-divider>
                                            </v-row>
                                        </v-card-text>
                                    </v-row>

                                    <v-progress-linear
                                        color="primary"
                                        :active="isLoadingPlantGrowthTemplate"
                                        :indeterminate="
                                            isLoadingPlantGrowthTemplate
                                        "
                                        height="2"
                                    ></v-progress-linear>

                                    <v-row
                                        v-for="(phase,
                                        index) in growthTask.phases"
                                        v-bind:key="index"
                                    >
                                        <v-col cols="3" sm="2">
                                            <v-text-field
                                                v-model="phase.phaseNo"
                                                label="Phase"
                                                outlined
                                                disabled
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="4" sm="2">
                                            <v-text-field
                                                v-model="phase.numDays"
                                                :rules="[rules.required]"
                                                type="number"
                                                min="1"
                                                label="#Days*"
                                                outlined
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="5" sm="2">
                                            <v-text-field
                                                v-model="phase.numPlantsPerM2"
                                                :rules="[rules.required]"
                                                type="number"
                                                min="1"
                                                label="#Plants / m2*"
                                                outlined
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="5" sm="2">
                                            <v-text-field
                                                :value="
                                                    calculateSpaceM2(
                                                        phase.numPlantsPerM2,
                                                        totalQuantity
                                                    )
                                                "
                                                type="number"
                                                label="Space m2*"
                                                outlined
                                                disabled
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete
                                                v-model="
                                                    phase.greenhouseDepartmentSectionNodeId
                                                "
                                                :items="
                                                    greenhouseDepartmentSections
                                                "
                                                item-value="id"
                                                item-text="fullName"
                                                :loading="
                                                    $apollo.queries.greenhouses
                                                        .loading
                                                "
                                                :rules="[rules.required]"
                                                label="Greenhouse*"
                                                class="subtitle-2"
                                                outlined
                                            >
                                            </v-autocomplete>
                                        </v-col>
                                    </v-row>
                                    <v-divider></v-divider>
                                </v-card>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn text rounded @click="step = 2"
                                        >Previous</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        @click="step3Next"
                                        >Continue</v-btn
                                    >
                                </v-card-actions>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 4: start date -->
                        <v-stepper-step step="4" :complete="step > 4"
                            >Start date</v-stepper-step
                        >

                        <v-stepper-content step="4" class="pt-0 pl-0">
                            <v-form ref="formStep4">
                                <v-card-text class="pt-0">
                                    <v-row class="mx-0">
                                        <v-col cols="12" class="pb-0">
                                            <v-radio-group
                                                v-model="startDateOption"
                                                row
                                            >
                                                <v-radio
                                                    label="Growth Start Date"
                                                    value="growth"
                                                    color="success"
                                                ></v-radio>
                                                <v-radio
                                                    label="Sales Start Date"
                                                    value="sales"
                                                    color="warning"
                                                ></v-radio>
                                            </v-radio-group>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-date-picker
                                                v-model="selectedStartDate"
                                                :allowed-dates="
                                                    functionAllowedDates
                                                "
                                                :events="functionCalanderEvents"
                                                first-day-of-week="1"
                                                locale-first-day-of-year="4"
                                                show-week
                                            ></v-date-picker>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-card flat>
                                                <!-- Growing -->
                                                <v-card-title>
                                                    <v-icon
                                                        small
                                                        color="green lighten-1"
                                                        class="mr-2"
                                                        >fiber_manual_record</v-icon
                                                    >Growing
                                                </v-card-title>

                                                <v-card-text>
                                                    <p
                                                        v-if="
                                                            growthTask.startDate !==
                                                                null
                                                        "
                                                    >
                                                        {{
                                                            growthStartDate
                                                                | moment("LL")
                                                        }}
                                                        ~
                                                        {{
                                                            growthEndDate
                                                                | moment("LL")
                                                        }}
                                                    </p>
                                                    <p v-else>
                                                        Please select start
                                                        date!
                                                    </p>
                                                </v-card-text>

                                                <!-- Sales -->
                                                <v-card-title>
                                                    <v-icon
                                                        small
                                                        color="orange"
                                                        class="mr-2"
                                                        >fiber_manual_record</v-icon
                                                    >Sales
                                                </v-card-title>

                                                <v-card-text>
                                                    <p
                                                        v-if="
                                                            growthTask.startDate !==
                                                                null
                                                        "
                                                    >
                                                        {{
                                                            salesStartDate
                                                                | moment("LL")
                                                        }}
                                                        ~
                                                        {{
                                                            salesEndDate
                                                                | moment("LL")
                                                        }}
                                                    </p>
                                                    <p v-else>
                                                        Please select start
                                                        date!
                                                    </p>
                                                </v-card-text>

                                                <v-card-actions
                                                    v-if="
                                                        selectedPlant !== null
                                                    "
                                                    class="mt-10"
                                                >
                                                    <v-spacer />
                                                    <v-btn
                                                        outlined
                                                        rounded
                                                        :disabled="isSaving"
                                                        @click="step = 3"
                                                        >Previous</v-btn
                                                    >
                                                    <v-btn
                                                        outlined
                                                        rounded
                                                        color="primary"
                                                        :loading="isSaving"
                                                        :disabled="
                                                            growthTask.startDate ===
                                                                null
                                                        "
                                                        @click="save"
                                                        >Add</v-btn
                                                    >
                                                    <!-- <v-btn
                                                        outlined
                                                        rounded
                                                        color="primary"
                                                        :loading="isSaving"
                                                        :disabled="
                                                            growthTask.startDate ===
                                                                null
                                                        "
                                                        @click="
                                                            saveAndAddAnother
                                                        "
                                                        >Save and Add
                                                        Another</v-btn
                                                    > -->
                                                </v-card-actions>

                                                <!-- Phase progress -->
                                                <!-- <v-card-title v-if="growthTask.startDate != null">
                                                    <v-icon
                                                        small
                                                        color="primary"
                                                        class="mr-2"
                                                    >insert_invitation</v-icon>Overview
                                                </v-card-title>

                                                <v-card-text
                                                    class="py-0"
                                                    v-if="growthTask.startDate != null"
                                                >
                                                    <v-slider
                                                        :value="currentPhaseNo - 1"
                                                        :tick-labels="generateTaskPhaseTickLabels(growthTask.numPhases)"
                                                        :color="currentPhaseColor"
                                                        track-color="grey lighten-3"
                                                        :max="growthTask.numPhases"
                                                        :step="1"
                                                        ticks="always"
                                                        tick-size="4"
                                                        readonly
                                                    ></v-slider>
                                                </v-card-text>-->
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-card-text>

                                <!-- <v-card-actions v-if="selectedPlant!==null">
                                    <v-spacer />
                                    <v-btn text rounded @click="step=3">Previous</v-btn>
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        :loading="isSaving"
                                        :disabled="growthTask.startDate === null"
                                        @click="save"
                                    >Save</v-btn>
                                </v-card-actions>-->
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 5: created -->
                        <v-stepper-step step="5" :complete="step > 5"
                            >Complete</v-stepper-step
                        >

                        <v-stepper-content step="5" class="pt-0 pl-0">
                            <GrowthTaskQrCode
                                :task="createdGrowthTask"
                            ></GrowthTaskQrCode>

                            <v-card-actions class="pb-5">
                                <v-spacer></v-spacer>
                                <v-spacer />
                                <v-btn outlined rounded @click="closeMainDialog"
                                    >Close</v-btn
                                >
                                <v-btn
                                    rounded
                                    outlined
                                    color="primary"
                                    v-print="'#printMe'"
                                    >Print</v-btn
                                >
                                <v-btn
                                    outlined
                                    rounded
                                    color="primary"
                                    @click="AddAnother"
                                    >Add Another</v-btn
                                >
                            </v-card-actions>
                        </v-stepper-content>
                    </v-stepper>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import moment from "moment";
import PlantConfigDialog from "@/components/plants/PlantConfigDialog.vue";
import GrowthTaskQrCode from "@/components/growth/GrowthTaskQrCode.vue";

export default {
    name: "growth-task-editor",
    props: ["dialog"],
    components: {
        PlantConfigDialog,
        GrowthTaskQrCode
    },

    apollo: {
        tenantPlants: {
            query: gql`
                query tenantPlants(
                    $first: Int
                    $last: Int
                    $before: String
                    $after: String
                    $orderBy: String
                    $search: String
                    $isArchived: Boolean
                ) {
                    tenantPlants(
                        first: $first
                        last: $last
                        before: $before
                        after: $after
                        search: $search
                        isArchived: $isArchived
                        orderBy: $orderBy
                    ) {
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                        }
                        edges {
                            node {
                                id
                                name
                                plantSpecies {
                                    id
                                    name
                                }
                                numProducts
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    first: 10,
                    isArchived: false
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.tenantPlants,
            skip() {
                return true;
            }

            // pollInterval: 300 // ms
        },

        suppliers: {
            query: gql`
                query suppliers {
                    suppliers {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.suppliers,
            skip() {
                return this.step != 2;
            }
            // pollInterval: 300 // ms
        },

        greenhouses: {
            query: gql`
                query greenhouses {
                    greenhouses {
                        edges {
                            node {
                                id
                                name
                                greenhouseDepartmentSet(
                                    isPlaceholder: false
                                    orderBy: "name"
                                ) {
                                    edges {
                                        node {
                                            id
                                            name
                                            greenhouseDepartmentSectionSet(
                                                isPlaceholder: false
                                                orderBy: "name"
                                            ) {
                                                edges {
                                                    node {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.greenhouses,
            skip() {
                return this.step != 3;
            }
            // pollInterval: 300 // ms
        }
    },

    data: function() {
        return {
            // isLoadingPlants: false,
            // isLoadingSuppliers: false,
            // isLoadingGreenhouseDepartmentSections: false,
            configPlantDialog: false,
            isSaving: false,
            isLoadingGrowthTask: false,
            isLoadingPlantGrowthTemplate: false,
            isImportPhasesFromTemplate: true,

            // numSaleDaysChoices: [1, 2, 3, 4, 5],
            numPhasesChoices: [1, 2, 3, 4, 5],

            defaultItemsPerPage: 10,
            dataTableOptions: {},
            page: 1,
            tenantPlants: {},

            step: 1,
            search: null,
            configingPlant: {},
            selectedPlant: null,
            supplierNodeId: null,
            supplierOrderNo: null,
            unitPurchasePrice: null,
            note: null,
            selectedTemplateNodeId: null,
            totalQuantity: null,
            // currentGreenhouseDepartmentSectionNodeId: null,
            currentPhaseNo: 1,
            // currentSpaceM2: null,

            startDateOption: "sales",
            selectedStartDate: null,

            suppliers: {},
            greenhouses: {},

            growthTask: {
                numPhases: 1,
                startDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null,
                        isCurrentPhase: false,
                        confirmedDate: null,
                        greenhouseDepartmentSectionNodeId: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    numDays: 3
                }
            },

            defaultGrowthTask: {
                numDays: 3,
                numPhases: 1,
                startDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null,
                        isCurrentPhase: false,
                        confirmedDate: null,
                        greenhouseDepartmentSectionNodeId: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    phaseNo: 1,
                    numDays: 3
                }
            },

            createdGrowthTask: null,

            headers: [
                {
                    text: "Plant Species",
                    align: "left",
                    sortable: true,
                    value: "node.plantSpecies.name"
                },
                {
                    text: "Plant",
                    align: "left",
                    sortable: true,
                    value: "node.name"
                },
                {
                    text: "#Products",
                    align: "left",
                    sortable: true,
                    value: "node.numProducts"
                },
                {
                    text: "Actions",
                    align: "center",
                    value: "action",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {
        // plants() {
        //     return this.$store.state.plants.plants;
        // },
        // suppliers() {
        //     return this.$store.state.suppliers.suppliers;
        // },
        // greenhouseDepartmentSections() {
        //     return this.$store.state.greenhouseDepartmentSections.greenhouseDepartmentSections.filter(
        //         item => {
        //             return !item.node.isPlaceholder;
        //         }
        //     );
        // },
        numSaleDaysChoices: function() {
            return Array(90)
                .fill(0)
                .map((_, i) => i + 1);
        },

        sortedSelectedPlantGrowthTemplates: function() {
            var _sorted = this.selectedPlant
                ? this.selectedPlant.plant.plantGrowthTemplateSet.edges.filter(
                      item => {
                          return (
                              item.node.potSize.id ==
                              this.selectedPlant.potSize.id
                          );
                      }
                  )
                : [];

            if (_sorted.length > 0) {
                _sorted = _sorted.sort((a, b) => {
                    if (a.node.potSize.potSizeCm === b.node.potSize.potSizeCm) {
                        return a.node.name.toLowerCase() ==
                            b.node.name.toLowerCase()
                            ? 0
                            : a.node.name.toLowerCase() >
                              b.node.name.toLowerCase()
                            ? 1
                            : -1;
                    }
                    return a.node.potSize.potSizeCm - b.node.potSize.potSizeCm;
                });
            }

            return _sorted;
        },

        greenhouseDepartmentSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        id: section.node.id,
                                        name: section.node.name,
                                        fullName:
                                            greenhouse.node.name +
                                            "-" +
                                            department.node.name +
                                            "-" +
                                            section.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return _sections;
        },

        // defaultGrowthTemplate() {
        //     var template = null;

        //     if (this.selectedPlant !== null) {
        //         template = this.selectedPlant.plant.plantGrowthTemplateSet.edges.find(
        //             item => {
        //                 return (
        //                     this.selectedPlant.potSize.id ===
        //                     item.node.potSize.id
        //                 );
        //             }
        //         );
        //     }

        //     return template;
        // },
        totalGrowthDays() {
            return this.growthTask.phases.reduce(
                (total, item) => parseInt(total) + parseInt(item.numDays || 0),
                0
            );
        },
        growthStartDate() {
            return new Date(this.growthTask.startDate);
        },
        growthEndDate() {
            return new Date(
                this.growthStartDate.getTime() +
                    (this.totalGrowthDays - 1) * 864e5
            );
        },
        salesStartDate() {
            return new Date(this.growthEndDate.getTime() + 864e5);
        },
        salesEndDate() {
            return new Date(
                this.salesStartDate.getTime() +
                    (this.growthTask.sales.numDays - 1) * 864e5
            );
        },
        growthTaskWithSalesPhases() {
            const today = new Date();

            var _phases = this.growthTask.phases.map(item => {
                return {
                    ...item,
                    spaceM2: this.calculateSpaceM2(
                        item.numPlantsPerM2,
                        this.totalQuantity
                    )
                };
            });

            // add sales phase: copy growth task's last phase [numPlantsPerM2] and [greenhouseDepartmentSectionNodeId]
            _phases.push({
                ...this.growthTask.sales,
                phaseNo: _phases.length + 1,
                numPlantsPerM2: _phases[_phases.length - 1].numPlantsPerM2,
                greenhouseDepartmentSectionNodeId:
                    _phases[_phases.length - 1]
                        .greenhouseDepartmentSectionNodeId,
                spaceM2: _phases[_phases.length - 1].spaceM2,
                startDate: moment(this.salesStartDate).format("YYYY-MM-DD"),
                endDate: moment(this.salesEndDate).format("YYYY-MM-DD"),
                isCurrentPhase: moment(this.salesStartDate).isSameOrBefore(
                    today,
                    "day"
                ),
                confirmedDate: moment(this.salesStartDate).isSameOrBefore(
                    today,
                    "day"
                )
                    ? moment(today).format("YYYY-MM-DD")
                    : null
            });

            return _phases;
        },
        currentPhaseColor() {
            const today = new Date();

            if (
                this.growthTask.startDate == null ||
                moment(this.growthStartDate).isAfter(today, "day")
            ) {
                return "grey";
            }

            return this.currentPhaseNo > this.growthTask.numPhases
                ? "orange"
                : "success";
        }
    },
    watch: {
        dataTableOptions: {
            handler() {
                this.refreshCurrentPlantsPage();
            },
            deep: true
        },
        "growthTask.numPhases"(val) {
            if (this.selectedTemplateNodeId === null) {
                if (val > this.growthTask.phases.length) {
                    for (
                        var i = this.growthTask.phases.length + 1;
                        i <= val;
                        i++
                    ) {
                        this.growthTask.phases.push({
                            isSalesPhase: false,
                            phaseNo: i,
                            numDays: null,
                            numPlantsPerM2: null
                        });
                    }
                } else if (val < this.growthTask.phases.length) {
                    this.growthTask.phases.splice(
                        val,
                        this.growthTask.phases.length - val
                    );
                }
            }
        },
        "growthTask.startDate"(val) {
            if (val !== null) {
                // refreshPhaseDateRange
                this.refreshPhaseDateRange();
            }
        },
        dialog(val) {
            if (val) {
                this.resetFormStep1();
                this.refreshCurrentPlantsPage();

                // this.fetchPlants();
                // this.fetchSuppliers();
                // this.fetchSections();
            }
        },
        selectedStartDate(val) {
            // if editing, don't process
            if (this.isLoadingGrowthTask) {
                return;
            }

            if (val !== null) {
                // refreshPhaseDateRange
                this.refreshPhaseDateRange();
            }
        },
        startDateOption(val) {
            // if editing, don't process
            if (this.isLoadingGrowthTask) {
                return;
            }
            if (val !== null && this.selectedStartDate) {
                // refreshPhaseDateRange
                this.refreshPhaseDateRange();
            }
        },
        selectedPlant(val) {
            if (val !== null) {
                this.resetFormStepPhases();
                this.supplierNodeId = val.plant.defaultSupplier
                    ? val.plant.defaultSupplier.id
                    : null;
            }
        },
        isImportPhasesFromTemplate() {
            this.resetFormStepPhases();
        },
        selectedTemplateNodeId(val) {
            if (val) {
                const selectedTemplate = this.selectedPlant.plant.plantGrowthTemplateSet.edges.find(
                    item => {
                        return item.node.id === val;
                    }
                );

                if (selectedTemplate !== null) {
                    this.growthTask.sales.numDays =
                        selectedTemplate.node.maxSalesDays;
                    this.growthTask.numPhases = selectedTemplate.node.numPhases;
                    // this.growthTask.phases = selectedTemplate.node.plantGrowthTemplatePhaseSet.edges.map(
                    //     item => {
                    //         return {
                    //             isSalesPhase: false,
                    //             phaseNo: item.node.phaseNo,
                    //             numDays: item.node.numDays,
                    //             numPlantsPerM2: item.node.numPlantsPerM2,
                    //             greenhouseDepartmentSectionNodeId: item.node
                    //                 .defaultGreenhouseDepartmentSection
                    //                 ? item.node
                    //                       .defaultGreenhouseDepartmentSection.id
                    //                 : null
                    //         };
                    //     }
                    // );

                    this.getPlantGrowthTemplate(selectedTemplate.node.id);
                }

                this.$refs.formStep3.resetValidation();
            } else {
                this.resetFormStepPhases();
            }
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        // initialize() {
        //     this.fetchPlants();
        // },

        getPlantGrowthTemplate(plantGrowthTemplateNodeId) {
            if (!plantGrowthTemplateNodeId) {
                return;
            }

            this.isLoadingPlantGrowthTemplate = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plantGrowthTemplate($id: ID!) {
                            plantGrowthTemplate(id: $id) {
                                id
                                name
                                numPhases
                                seasonEffectMonths
                                totalGrowthDays
                                maxSalesDays
                                potSize {
                                    id
                                    potSizeCm
                                }
                                plant {
                                    id
                                    name
                                }
                                plantGrowthTemplatePhaseSet {
                                    edges {
                                        node {
                                            id
                                            phaseNo
                                            numDays
                                            numPlantsPerM2
                                            defaultGreenhouseDepartmentSection {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                                createdAt
                                updatedAt
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plantGrowthTemplateNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    let plantGrowthTemplate = response.data.plantGrowthTemplate;

                    this.growthTask.phases = plantGrowthTemplate.plantGrowthTemplatePhaseSet.edges.map(
                        item => {
                            return {
                                isSalesPhase: false,
                                phaseNo: item.node.phaseNo,
                                numDays: item.node.numDays,
                                numPlantsPerM2: item.node.numPlantsPerM2,
                                greenhouseDepartmentSectionNodeId: item.node
                                    .defaultGreenhouseDepartmentSection
                                    ? item.node
                                          .defaultGreenhouseDepartmentSection.id
                                    : null
                            };
                        }
                    );
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    // this.isSavingOrderPrintStatus = false;
                    this.isLoadingPlantGrowthTemplate = false;
                });
        },

        resetFormStep1() {
            this.step = 1;
            this.search = "";
            this.configingPlant = null;
            this.selectedPlant = null;
            this.supplierNodeId = null;
            this.supplierOrderNo = null;
            this.note = null;
            this.selectedTemplateNodeId = null;
            this.totalQuantity = null;
            this.unitPurchasePrice = null;
            // this.currentGreenhouseDepartmentSectionNodeId = null;
            this.currentPhaseNo = 1;
            this.isImportPhasesFromTemplate = true;
            // this.currentSpaceM2 = null;
            this.selectedStartDate = null;
            this.createdGrowthTask = null;

            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
        },

        resetFormStepPhases() {
            this.isSaving = false;
            this.selectedTemplateNodeId = null;
            this.growthTask = JSON.parse(
                JSON.stringify(this.defaultGrowthTask)
            );

            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
        },

        step2Next() {
            if (!this.$refs.formStep2.validate()) {
                return;
            }

            this.step = 3;
        },

        step3Next() {
            if (!this.$refs.formStep3.validate()) {
                return;
            }

            if (this.growthStartDate) {
                this.refreshPhaseDateRange();
            }

            this.step = 4;
        },

        generateTaskPhaseTickLabels(numPhases) {
            var tickLabels = [];

            var i;
            for (i = 1; i <= numPhases; i++) {
                tickLabels.push("P" + i);
            }

            tickLabels.push("Sale");

            return tickLabels;
        },

        // fetchPlants() {
        //     this.isLoadingPlants = true;

        //     // fetch plants
        //     this.$store
        //         .dispatch("plants/fetchPlants")
        //         .then(() => {})
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //         })
        //         .finally(() => (this.isLoadingPlants = false));
        // },

        onClearSearch() {
            this.search = "";

            this.refreshCurrentPlantsPage();
        },

        refreshCurrentPlantsPage() {
            const {
                sortBy,
                sortDesc,
                page,
                itemsPerPage
            } = this.dataTableOptions;

            var _first = itemsPerPage;
            var _last = null;
            var _before = null;
            var _after = null;
            var _orderBy = null;
            const _isArchived = false;

            if (page < this.page) {
                _first = null;
                _last = itemsPerPage;
                _before = this.tenantPlants.pageInfo.startCursor;
                _after = null;
            }

            if (page > this.page) {
                _first = itemsPerPage;
                _last = null;
                _before = null;
                _after = this.tenantPlants.pageInfo.endCursor;
            }

            if (sortBy) {
                switch (sortBy[0]) {
                    case "node.plantSpecies.name":
                        _orderBy = "plantSpeciesName";
                        break;
                    case "node.name":
                        _orderBy = "plantName";
                        break;
                    case "node.numProducts":
                        _orderBy = "numProducts";
                        break;
                    default:
                        _orderBy = null;
                        break;
                }

                if (sortDesc[0] === true) {
                    _orderBy = "-" + _orderBy;
                }
            }

            this.$apollo.queries.tenantPlants.setVariables({
                first: _first,
                last: _last,
                before: _before,
                after: _after,
                orderBy: _orderBy,
                search: this.search,
                isArchived: _isArchived
            });

            // set skip false to execute the query
            this.$apollo.queries.tenantPlants.skip = false;
            this.$apollo.queries.tenantPlants.refresh();

            // update this.page
            this.page = page;
        },

        // fetchSuppliers() {
        //     this.isLoadingSuppliers = true;

        //     // fetch suppliers
        //     this.$store
        //         .dispatch("suppliers/fetchSuppliers")
        //         .then(() => {})
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //         })
        //         .finally(() => (this.isLoadingSuppliers = false));
        // },

        // fetchSections() {
        //     // fetch section
        //     this.isLoadingGreenhouseDepartmentSections = true;
        //     this.$store
        //         .dispatch(
        //             "greenhouseDepartmentSections/fetchGreenhouseDepartmentSections"
        //         )
        //         .then(() => {
        //             // console.log(this.greenhouseDepartmentSections);
        //         })
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //         })
        //         .finally(
        //             () => (this.isLoadingGreenhouseDepartmentSections = false)
        //         );
        // },

        calculateSpaceM2(numPlantsPerM2, quantity) {
            return numPlantsPerM2 > 0
                ? Math.ceil(quantity / numPlantsPerM2)
                : 0;
        },

        onConfigPlant(item) {
            // // sort plantColors
            // item.node.plantColorSet.edges.sort((a, b) => {
            //     return a.node.color.color.toLowerCase() ==
            //         b.node.color.color.toLowerCase()
            //         ? 0
            //         : a.node.color.color.toLowerCase() >
            //           b.node.color.color.toLowerCase()
            //         ? 1
            //         : -1;
            // });

            // // sort plantPotSizes
            // item.node.plantPotSizeSet.edges.sort((a, b) => {
            //     return a.node.potSize.potSizeCm == b.node.potSize.potSizeCm
            //         ? 0
            //         : a.node.potSize.potSizeCm > b.node.potSize.potSizeCm
            //         ? 1
            //         : -1;
            // });

            // // sort plantEnvironments
            // item.node.plantEnvironmentSet.edges.sort((a, b) => {
            //     return a.node.environment.environment.toLowerCase() ==
            //         b.node.environment.environment.toLowerCase()
            //         ? 0
            //         : a.node.environment.environment.toLowerCase() >
            //           b.node.environment.environment.toLowerCase()
            //         ? 1
            //         : -1;
            // });

            // // sort plantTags
            // item.node.plantTagSet.edges.sort((a, b) => {
            //     return a.node.tag.orderId == b.node.tag.orderId
            //         ? 0
            //         : a.node.tag.orderId > b.node.tag.orderId
            //         ? 1
            //         : -1;
            // });

            this.configingPlant = item;
            this.configPlantDialog = true;
        },

        onPlantConfirmed(item) {
            this.selectedPlant = null;
            this.selectedPlant = Object.assign({}, item);

            this.configPlantDialog = false;
            this.configingPlant = null;
            this.step = 2;
        },

        // refresh phase start date and end date when select task start date in date picker
        refreshPhaseDateRange() {
            var accumulateDays = 0;
            const today = new Date();

            // default phase no is 1
            this.currentPhaseNo = 1;

            // refresh only after selected start date
            if (this.selectedStartDate == null) {
                return;
            }

            // select growth start date
            if (this.startDateOption == "growth") {
                this.growthTask.startDate = this.selectedStartDate;
            }
            // select sales start date
            else {
                let totalGrowthDays = this.growthTask.phases.reduce(
                    (accumulator, item) => accumulator + parseInt(item.numDays),
                    0
                );

                this.growthTask.startDate =
                    new Date(this.selectedStartDate) - totalGrowthDays * 864e5;
            }

            this.growthTask.phases
                .sort((a, b) => a.phaseNo - b.phaseNo)
                .forEach(item => {
                    const _startDate =
                        this.growthStartDate.getTime() + accumulateDays * 864e5;
                    item.startDate = moment(_startDate).format("YYYY-MM-DD");

                    const _endDate = _startDate + (item.numDays - 1) * 864e5;
                    item.endDate = moment(_endDate).format("YYYY-MM-DD");

                    // if item phaseNo = 1, and not started yet, then current Phase is 1
                    if (
                        item.phaseNo === 1 &&
                        moment(_startDate).isAfter(today, "day")
                    ) {
                        item.isCurrentPhase = true;
                        item.confirmedDate = null;
                        this.currentPhaseNo = item.phaseNo;
                    }
                    // if today is between current item start date and end date
                    else if (
                        moment(_startDate).isSameOrBefore(today, "day") &&
                        moment(_endDate).isSameOrAfter(today, "day")
                    ) {
                        item.isCurrentPhase = true;
                        item.confirmedDate = moment(today).format("YYYY-MM-DD");
                        this.currentPhaseNo = item.phaseNo;
                    } else {
                        item.isCurrentPhase = false;
                        item.confirmedDate = null;
                    }

                    accumulateDays += parseInt(item.numDays);
                });

            if (moment(this.salesStartDate).isSameOrBefore(today, "day")) {
                this.currentPhaseNo = this.growthTask.phases.length + 1;
            }

            // console.log("currentPhaseNo: " + this.currentPhaseNo);
            // console.log(this.growthTask.phases);
        },

        // calendar functions
        functionAllowedDates() {
            // const calendarDate = new Date(date);
            // // // only monday is allowed to be selected
            // // return calendarDate.getDay() === 1;

            // return moment(calendarDate).isSameOrAfter(
            //     moment(new Date()).startOf("isoweek"),
            //     "day"
            // );

            // allow all days
            return true;
        },

        functionCalanderEvents(date) {
            if (this.growthTask.startDate !== null) {
                const calendarDate = new Date(date);

                // growing
                if (
                    calendarDate >= this.growthStartDate &&
                    calendarDate <= this.growthEndDate
                ) {
                    return "green lighten-1";
                }

                // saling
                if (
                    calendarDate >= this.salesStartDate &&
                    calendarDate <= this.salesEndDate
                ) {
                    return "orange";
                }

                return false;
            } else {
                return false;
            }
        },

        save() {
            if (!this.$refs.formStep4.validate()) {
                return;
            }

            this.isSaving = true;

            this.refreshPhaseDateRange();

            // var _phases = this.growthTask.phases.map(item => {
            //     return {
            //         ...item,
            //         spaceM2: this.calculateSpaceM2(
            //             item.numPlantsPerM2,
            //             this.totalQuantity
            //         )
            //     };
            // });

            // // add sales phase: copy growth task's last phase [numPlantsPerM2] and [greenhouseDepartmentSectionNodeId]
            // _phases.push({
            //     ...this.growthTask.sales,
            //     numPlantsPerM2: _phases[_phases.length - 1].numPlantsPerM2,
            //     greenhouseDepartmentSectionNodeId:
            //         _phases[_phases.length - 1]
            //             .greenhouseDepartmentSectionNodeId,
            //     spaceM2: _phases[_phases.length - 1].spaceM2,
            //     startDate: moment(this.salesStartDate).format("YYYY-MM-DD"),
            //     endDate: moment(this.salesEndDate).format("YYYY-MM-DD")
            // });

            // prepare api call payload
            var payload = {
                plantNodeId: this.selectedPlant.plant.id,
                colorNodeId: this.selectedPlant.color.id,
                potSizeNodeId: this.selectedPlant.potSize.id,
                environmentNodeId: this.selectedPlant.environment.id,
                tagNodeId: this.selectedPlant.tag
                    ? this.selectedPlant.tag.id
                    : null,
                plantGrowthTemplateNodeId: this.selectedTemplateNodeId,
                // currentGreenhouseDepartmentSectionNodeId: this
                //     .currentGreenhouseDepartmentSectionNodeId,
                // currentPhaseNo: this.currentPhaseNo,
                // currentSpaceM2: this.currentSpaceM2,
                supplierNodeId: this.supplierNodeId,
                numPhases: this.growthTask.numPhases,
                totalGrowthDays: this.totalGrowthDays,
                maxSalesDays: this.growthTask.sales.numDays,
                growthStartDate: moment(this.growthStartDate).format(
                    "YYYY-MM-DD"
                ),
                growthEndDate: moment(this.growthEndDate).format("YYYY-MM-DD"),
                salesStartDate: moment(this.salesStartDate).format(
                    "YYYY-MM-DD"
                ),
                salesEndDate: moment(this.salesEndDate).format("YYYY-MM-DD"),
                lockedDate: null,
                lockedComment: null,
                lockedForCustomerNodeId: null,
                closedDate: null,
                closedReason: null,
                supplierOrderNo: this.supplierOrderNo,
                note: this.note,
                totalQuantity: parseInt(this.totalQuantity),
                totalInventory: parseInt(this.totalQuantity),
                unitPurchasePrice: parseFloat(this.unitPurchasePrice),
                currency: "EUR",
                phases: this.growthTaskWithSalesPhases
            };

            // console.log(this.growthTaskWithSalesPhases);
            // console.log(payload);

            this.$store
                .dispatch("growthTasks/createGrowthTask", payload)
                .then(response => {
                    this.createdGrowthTask = response;

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Growth task is created!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);

                    this.isSaving = false;

                    this.step = 5;

                    this.$emit("item-created", response);

                    // this.closeMainDialog();

                    //TODO: print qr code
                })
                .catch(error => {
                    /*handle errors*/
                    console.log(error);

                    let _error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: _error
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);

                    this.isSaving = false;
                })
                .finally();
        },

        // saveAndAddAnother() {
        //     if (!this.$refs.formStep4.validate()) {
        //         return;
        //     }

        //     this.isSaving = true;

        //     this.refreshPhaseDateRange();

        //     // var _phases = this.growthTask.phases.map(item => {
        //     //     return {
        //     //         ...item,
        //     //         spaceM2: this.calculateSpaceM2(
        //     //             item.numPlantsPerM2,
        //     //             this.totalQuantity
        //     //         )
        //     //     };
        //     // });

        //     // // add sales phase: copy growth task's last phase [numPlantsPerM2] and [greenhouseDepartmentSectionNodeId]
        //     // _phases.push({
        //     //     ...this.growthTask.sales,
        //     //     numPlantsPerM2: _phases[_phases.length - 1].numPlantsPerM2,
        //     //     greenhouseDepartmentSectionNodeId:
        //     //         _phases[_phases.length - 1]
        //     //             .greenhouseDepartmentSectionNodeId,
        //     //     spaceM2: _phases[_phases.length - 1].spaceM2,
        //     //     startDate: moment(this.salesStartDate).format("YYYY-MM-DD"),
        //     //     endDate: moment(this.salesEndDate).format("YYYY-MM-DD")
        //     // });

        //     // prepare api call payload
        //     var payload = {
        //         plantNodeId: this.selectedPlant.plant.id,
        //         colorNodeId: this.selectedPlant.color.id,
        //         potSizeNodeId: this.selectedPlant.potSize.id,
        //         environmentNodeId: this.selectedPlant.environment.id,
        //         tagNodeId: this.selectedPlant.tag
        //             ? this.selectedPlant.tag.id
        //             : null,
        //         plantGrowthTemplateNodeId: this.selectedTemplateNodeId,
        //         // currentGreenhouseDepartmentSectionNodeId: this
        //         //     .currentGreenhouseDepartmentSectionNodeId,
        //         // currentPhaseNo: this.currentPhaseNo,
        //         // currentSpaceM2: this.currentSpaceM2,
        //         supplierNodeId: this.supplierNodeId,
        //         numPhases: this.growthTask.numPhases,
        //         totalGrowthDays: this.totalGrowthDays,
        //         maxSalesDays: this.growthTask.sales.numDays,
        //         growthStartDate: moment(this.growthStartDate).format(
        //             "YYYY-MM-DD"
        //         ),
        //         growthEndDate: moment(this.growthEndDate).format("YYYY-MM-DD"),
        //         salesStartDate: moment(this.salesStartDate).format(
        //             "YYYY-MM-DD"
        //         ),
        //         salesEndDate: moment(this.salesEndDate).format("YYYY-MM-DD"),
        //         lockedDate: null,
        //         lockedComment: null,
        //         lockedForCustomerNodeId: null,
        //         closedDate: null,
        //         closedReason: null,
        //         supplierOrderNo: this.supplierOrderNo,
        //         note: this.note,
        //         totalQuantity: parseInt(this.totalQuantity),
        //         totalInventory: parseInt(this.totalQuantity),
        //         unitPurchasePrice: parseFloat(this.unitPurchasePrice),
        //         currency: "EUR",
        //         phases: this.growthTaskWithSalesPhases
        //     };

        //     // console.log(this.growthTaskWithSalesPhases);
        //     // console.log(payload);

        //     this.$store
        //         .dispatch("growthTasks/createGrowthTask", payload)
        //         .then(response => {
        //             // show snackbar
        //             const payload = {
        //                 color: "success",
        //                 message: `Growth task is created!`
        //             };
        //             this.$store.dispatch("snackbar/showMessage", payload);

        //             this.isSaving = false;

        //             this.$emit("item-created", response);

        //             this.resetWholeDialog();
        //         })
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);

        //             let _error = error.graphQLErrors
        //                 .map(error => error.message)
        //                 .join(", ");

        //             // show snackbar
        //             const payload = {
        //                 color: "error",
        //                 message: _error
        //             };
        //             this.$store.dispatch("snackbar/showMessage", payload);

        //             this.isSaving = false;
        //         })
        //         .finally();
        // },

        AddAnother() {
            this.resetWholeDialog();
        },

        resetWholeDialog() {
            this.step = 1;
            this.resetFormStep1();
            this.resetFormStepPhases();
            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
            if (this.$refs.formStep4) {
                this.$refs.formStep4.resetValidation();
            }
        },

        closeMainDialog() {
            this.step = 1;
            this.resetFormStep1();
            this.resetFormStepPhases();
            this.$emit("update:dialog", false);
            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
            if (this.$refs.formStep4) {
                this.$refs.formStep4.resetValidation();
            }
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        }
    }
};
</script>


<style scoped>
.box-border {
    border-style: solid;
    padding: 30px;
}
</style>
